// F-Meeting-1 Wave 3a: Cascade work item shape returned by /api/v1/outcomes/:id/cascade-work-items
export interface CascadeWorkItemDTO {
  id: string;
  type: string;
  title: string;
  status: string;
  dueDate: string | null;
  ownerActorId: string | null;
  assigneeName: string | null;
  domainId: string;
  domainName: string | null;
  parentMeetingId: string | null;
  cascadeAnnualOutcomeIds: string[] | null;
  createdAt: string;
}

import type {
  RorosOutcomeDTO,
  RorosMetricDTO,
  RorosMetricValueDTO,
  RorosPrincipleDTO,
  RorosRoutineArtifactDTO,
  RorosInitiativeDTO,
  RorosKnowledgeItemDTO,
  RorosLearningItemDTO,
  CreateOutcomePayload,
  UpdateOutcomePayload,
  CreateMetricPayload,
  UpdateMetricPayload,
  UpdateMetricValuePayload,
  CreatePrinciplePayload,
  UpdatePrinciplePayload,
  CreateRoutineArtifactPayload,
  UpdateRoutineArtifactPayload,
  CreateInitiativePayload,
  UpdateInitiativePayload,
  CreateKnowledgeItemPayload,
  UpdateKnowledgeItemPayload,
  CreateLearningItemPayload,
  UpdateLearningItemPayload,
  MetricSuggestion,
} from '@/types/roros/artifacts';
import { fetchJson, mutate, qs } from './base-roros.service';

// ─── Artifacts (domain-scoped) ──────────────────────────────────────────────

export interface RorosOutcomeDraftDTO {
  title: string;
  description: string;
  time_horizon: string;
  definition_of_done: string;
  target_date: string | null;
  draftedByActorId: string;
  draftedByActorName: string;
  sotContextTitles: string[];
}

export const RorosArtifactService = {
  // — Outcomes —
  // J7-W1 — AI Draft Outcome (PRD §12.3.10 task 10). Returns a 4-field draft.
  // The client prefills OutcomeFormDialog; the user reviews + commits manually.
  draftOutcome: (payload: { prompt: string; domainId?: string }) =>  // A1
    mutate<RorosOutcomeDraftDTO>(`/api/v1/artifacts/outcomes/draft`, 'POST', payload),
  // F5 — AI Metric Helper: suggest 1–3 candidate metrics for an outcome.
  suggestMetric: (payload: { title: string; description?: string; definition_of_done: string; domainId: string }) =>  // A1
    mutate<{ suggestions: MetricSuggestion[] }>(`/api/v1/artifacts/outcomes/suggest-metric`, 'POST', payload),
  listOutcomes: (domainId: string, params?: Record<string, string>) =>
    fetchJson<RorosOutcomeDTO[]>(`/api/v1/domains/${domainId}/outcomes${qs(params)}`),
  createOutcome: (domainId: string, payload: CreateOutcomePayload) =>
    mutate<RorosOutcomeDTO>(`/api/v1/domains/${domainId}/outcomes`, 'POST', payload),
  getOutcome: (domainId: string, id: string) =>
    fetchJson<RorosOutcomeDTO>(`/api/v1/domains/${domainId}/outcomes/${id}`),
  // F-Meeting-1 Wave 3a: org-scoped endpoints (not domain-scoped)
  listAnnualOutcomes: (params?: Record<string, string>) =>
    fetchJson<RorosOutcomeDTO[]>(`/api/v1/outcomes${qs(params)}`),
  listOutcomeCascadeWorkItems: (outcomeId: string) =>
    fetchJson<CascadeWorkItemDTO[]>(`/api/v1/outcomes/${outcomeId}/cascade-work-items`),
  updateOutcome: (domainId: string, id: string, payload: UpdateOutcomePayload) =>
    mutate<RorosOutcomeDTO>(`/api/v1/domains/${domainId}/outcomes/${id}`, 'PUT', payload),
  deleteOutcome: (domainId: string, id: string) =>
    mutate(`/api/v1/domains/${domainId}/outcomes/${id}`, 'DELETE'),

  // — Metrics —
  /** J7-W2 — cross-domain listing for the Role Contract editor's metric picker. */
  listAllMetricsForOrg: (params?: Record<string, string>) =>
    fetchJson<RorosMetricDTO[]>(`/api/v1/metrics${qs(params)}`),
  listMetrics: (domainId: string, params?: Record<string, string>) =>
    fetchJson<RorosMetricDTO[]>(`/api/v1/domains/${domainId}/metrics${qs(params)}`),
  createMetric: (domainId: string, payload: CreateMetricPayload) =>
    mutate<RorosMetricDTO>(`/api/v1/domains/${domainId}/metrics`, 'POST', payload),
  getMetric: (domainId: string, id: string) =>
    fetchJson<RorosMetricDTO>(`/api/v1/domains/${domainId}/metrics/${id}`),
  updateMetric: (domainId: string, id: string, payload: UpdateMetricPayload) =>
    mutate<RorosMetricDTO>(`/api/v1/domains/${domainId}/metrics/${id}`, 'PUT', payload),
  deleteMetric: (domainId: string, id: string) =>
    mutate(`/api/v1/domains/${domainId}/metrics/${id}`, 'DELETE'),
  updateMetricValue: (domainId: string, id: string, payload: UpdateMetricValuePayload) =>
    mutate<RorosMetricDTO>(`/api/v1/domains/${domainId}/metrics/${id}/value`, 'PUT', payload),
  getMetricHistory: (domainId: string, id: string, limit?: number) =>
    fetchJson<RorosMetricValueDTO[]>(
      `/api/v1/domains/${domainId}/metrics/${id}/history${limit ? `?limit=${limit}` : ''}`,
    ),
  getMetricOutcomes: (domainId: string, id: string) =>
    fetchJson<RorosOutcomeDTO[]>(`/api/v1/domains/${domainId}/metrics/${id}/outcomes`),
  linkMetricOutcome: (domainId: string, id: string, payload: { outcome_id: string }) =>
    mutate(`/api/v1/domains/${domainId}/metrics/${id}/outcomes`, 'POST', payload),
  unlinkMetricOutcome: (domainId: string, id: string, outcomeId: string) =>
    mutate(`/api/v1/domains/${domainId}/metrics/${id}/outcomes/${outcomeId}`, 'DELETE'),

  // — Principles —
  /** J7-W4 task 6b — cross-domain principles listing for the new PrinciplesPage. */
  listAllPrinciplesForOrg: (params?: Record<string, string>) =>
    fetchJson<RorosPrincipleDTO[]>(`/api/v1/principles${qs(params)}`),
  listPrinciples: (domainId: string, params?: Record<string, string>) =>
    fetchJson<RorosPrincipleDTO[]>(`/api/v1/domains/${domainId}/principles${qs(params)}`),
  createPrinciple: (domainId: string, payload: CreatePrinciplePayload) =>
    mutate<RorosPrincipleDTO>(`/api/v1/domains/${domainId}/principles`, 'POST', payload),
  getPrinciple: (domainId: string, id: string) =>
    fetchJson<RorosPrincipleDTO>(`/api/v1/domains/${domainId}/principles/${id}`),
  updatePrinciple: (domainId: string, id: string, payload: UpdatePrinciplePayload) =>
    mutate<RorosPrincipleDTO>(`/api/v1/domains/${domainId}/principles/${id}`, 'PUT', payload),
  deletePrinciple: (domainId: string, id: string) =>
    mutate(`/api/v1/domains/${domainId}/principles/${id}`, 'DELETE'),

  // — Routines (cross-domain, NAV-3) —
  listAllRoutines: (params?: Record<string, string>) =>
    fetchJson<RorosRoutineArtifactDTO[]>(`/api/v1/routines${qs(params)}`),

  // — Routines (domain-scoped) —
  listRoutineArtifacts: (domainId: string, params?: Record<string, string>) =>
    fetchJson<RorosRoutineArtifactDTO[]>(`/api/v1/domains/${domainId}/routines${qs(params)}`),
  createRoutineArtifact: (domainId: string, payload: CreateRoutineArtifactPayload) =>
    mutate<RorosRoutineArtifactDTO>(`/api/v1/domains/${domainId}/routines`, 'POST', payload),
  getRoutineArtifact: (domainId: string, id: string) =>
    fetchJson<RorosRoutineArtifactDTO>(`/api/v1/domains/${domainId}/routines/${id}`),
  updateRoutineArtifact: (domainId: string, id: string, payload: UpdateRoutineArtifactPayload) =>
    mutate<RorosRoutineArtifactDTO>(`/api/v1/domains/${domainId}/routines/${id}`, 'PUT', payload),
  deleteRoutineArtifact: (domainId: string, id: string) =>
    mutate(`/api/v1/domains/${domainId}/routines/${id}`, 'DELETE'),
  archiveRoutineArtifact: (id: string) =>
    mutate<RorosRoutineArtifactDTO>(`/api/v1/routines/${id}/archive`, 'POST'),

  // — Initiatives —
  listInitiatives: (domainId: string, params?: Record<string, string>) =>
    fetchJson<RorosInitiativeDTO[]>(`/api/v1/domains/${domainId}/initiatives${qs(params)}`),
  createInitiative: (domainId: string, payload: CreateInitiativePayload) =>
    mutate<RorosInitiativeDTO>(`/api/v1/domains/${domainId}/initiatives`, 'POST', payload),
  getInitiative: (domainId: string, id: string) =>
    fetchJson<RorosInitiativeDTO>(`/api/v1/domains/${domainId}/initiatives/${id}`),
  updateInitiative: (domainId: string, id: string, payload: UpdateInitiativePayload) =>
    mutate<RorosInitiativeDTO>(`/api/v1/domains/${domainId}/initiatives/${id}`, 'PUT', payload),
  deleteInitiative: (domainId: string, id: string) =>
    mutate(`/api/v1/domains/${domainId}/initiatives/${id}`, 'DELETE'),
  getInitiativeInstances: (domainId: string, id: string) =>
    fetchJson<RorosInitiativeDTO[]>(`/api/v1/domains/${domainId}/initiatives/${id}/instances`),
  /** J6 — Convert a one-time initiative into a routine. Creates the routine
   *  and atomically links it via convert_to_routine_id. */
  convertInitiativeToRoutine: (domainId: string, id: string) =>
    mutate<{ initiative: RorosInitiativeDTO; routine: { id: string; title: string; status: string; domainId: string } }>(
      `/api/v1/domains/${domainId}/initiatives/${id}/convert-to-routine`,
      'POST',
    ),

  // — Knowledge —
  listKnowledge: (domainId: string, params?: Record<string, string>) =>
    fetchJson<RorosKnowledgeItemDTO[]>(`/api/v1/domains/${domainId}/knowledge${qs(params)}`),
  createKnowledge: (domainId: string, payload: CreateKnowledgeItemPayload) =>
    mutate<RorosKnowledgeItemDTO>(`/api/v1/domains/${domainId}/knowledge`, 'POST', payload),
  getKnowledge: (domainId: string, id: string) =>
    fetchJson<RorosKnowledgeItemDTO>(`/api/v1/domains/${domainId}/knowledge/${id}`),
  updateKnowledge: (domainId: string, id: string, payload: UpdateKnowledgeItemPayload) =>
    mutate<RorosKnowledgeItemDTO>(`/api/v1/domains/${domainId}/knowledge/${id}`, 'PUT', payload),
  deleteKnowledge: (domainId: string, id: string) =>
    mutate(`/api/v1/domains/${domainId}/knowledge/${id}`, 'DELETE'),
  listSystemKnowledge: () =>
    fetchJson<RorosKnowledgeItemDTO[]>('/api/v1/domains/system/knowledge'),
  /** J7-W4 task 6a (ART-02) — flip last_reviewed_at + review_due_at, emit GOV-05 audit.
   *  Wave 4 F3 deferred fix (Wave 7): domain-scoped route so domainScopedWriteChain applies. */
  markKnowledgeReviewed: (domainId: string, id: string) =>
    mutate<RorosKnowledgeItemDTO>(`/api/v1/domains/${domainId}/knowledge/${id}/mark-reviewed`, 'POST'),
  /** J7-W4 task 6a — surface items past or near the freshness deadline. */
  listKnowledgeDueForReview: (withinDays: number = 0) =>
    fetchJson<RorosKnowledgeItemDTO[]>(`/api/v1/knowledge/due-for-review?withinDays=${withinDays}`),
  /** S-1 — Force re-ingest of attached documents, bypassing the R1 idempotency skip-guard. */
  reprocessKnowledgeDocument: (domainId: string, id: string) =>
    mutate<{ queued: boolean; reason?: string }>(`/api/v1/domains/${domainId}/knowledge/${id}/reprocess`, 'POST'),

  // — Learning —
  listLearning: (domainId: string, params?: Record<string, string>) =>
    fetchJson<RorosLearningItemDTO[]>(`/api/v1/domains/${domainId}/learning${qs(params)}`),
  createLearning: (domainId: string, payload: CreateLearningItemPayload) =>
    mutate<RorosLearningItemDTO>(`/api/v1/domains/${domainId}/learning`, 'POST', payload),
  getLearning: (domainId: string, id: string) =>
    fetchJson<RorosLearningItemDTO>(`/api/v1/domains/${domainId}/learning/${id}`),
  updateLearning: (domainId: string, id: string, payload: UpdateLearningItemPayload) =>
    mutate<RorosLearningItemDTO>(`/api/v1/domains/${domainId}/learning/${id}`, 'PUT', payload),
  deleteLearning: (domainId: string, id: string) =>
    mutate(`/api/v1/domains/${domainId}/learning/${id}`, 'DELETE'),
};

// ─── Persona Knowledge (pin/deny) ─────────────────────────────────────────────
// AC-K-8/K-9: Wave-4 pin/deny API consumed by persona Documents tab.

export interface PersonaPinDenyDTO {
  id: string;
  orgId: string;
  personaActorId: string;
  entityType: string;
  entityId: string;
  mode: 'pin' | 'deny';
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** R2: title of the knowledge item (entity_type='knowledge' only); null for other entity types. */
  itemTitle: string | null;
  /** AC-21/AC-25: description of the knowledge item; null if not set or non-knowledge entity. */
  itemDescription: string | null;
  /** AC-21/AC-25: item_type of the knowledge item; null if not set or non-knowledge entity. */
  itemType: string | null;
}

export interface PersonaReachableCountDTO {
  total: number;
  pinned: number;
  viaDomains: number;
}

export const RorosPersonaKnowledgeService = {
  /** List pin or deny bindings for a persona. */
  listPinDeny: (orgId: string, personaId: string, mode?: 'pin' | 'deny') => {
    const params = mode ? `?mode=${mode}` : '';
    return fetchJson<PersonaPinDenyDTO[]>(`/api/orgs/${orgId}/knowledge/personas/${personaId}/pin-deny${params}`);
  },

  /** Create a pin or deny binding (MANAGE_KNOWLEDGE-gated). */
  createPinDeny: (
    orgId: string,
    personaId: string,
    payload: { entityId: string; entityType: string; mode: 'pin' | 'deny' },
  ) =>
    mutate<PersonaPinDenyDTO>(
      `/api/orgs/${orgId}/knowledge/personas/${personaId}/pin-deny`,
      'POST',
      payload,
    ),

  /** Remove a binding by ID. */
  deletePinDeny: (orgId: string, personaId: string, bindId: string) =>
    mutate(`/api/orgs/${orgId}/knowledge/personas/${personaId}/pin-deny/${bindId}`, 'DELETE'),

  /** Get reachable-count { total, pinned, viaDomains } for a persona. */
  reachableCount: (orgId: string, personaId: string) =>
    fetchJson<PersonaReachableCountDTO>(
      `/api/orgs/${orgId}/knowledge/personas/${personaId}/reachable-count`,
    ),
};
